import type { Profile, SyncedTree } from '../../lib/types';

// Pure helpers for editing the synced tree's profile list. Each returns a
// new tree (or the original when there's nothing to change). Refusal cases
// (deleting the last profile) return null so the caller can show feedback.

export function addProfile(tree: SyncedTree, profile: Profile): SyncedTree {
  return {
    ...tree,
    profiles: [...tree.profiles, profile],
    meta: { ...tree.meta, lastWriteAt: profile.createdAt },
  };
}

export function updateProfile(
  tree: SyncedTree,
  profileId: string,
  patch: Partial<Pick<Profile, 'name' | 'avatarEmoji' | 'theme' | 'color'>>,
  now: string = new Date().toISOString(),
): SyncedTree {
  const profiles = tree.profiles.map((p) =>
    p.id === profileId ? { ...p, ...patch, updatedAt: now } : p,
  );
  return {
    ...tree,
    profiles,
    meta: { ...tree.meta, lastWriteAt: now },
  };
}

// Returns null when the request would leave the family with zero profiles —
// per plan/03 §Profile editor: "Refuse if it's the last profile."
export function removeProfile(
  tree: SyncedTree,
  profileId: string,
  now: string = new Date().toISOString(),
): SyncedTree | null {
  if (tree.profiles.length <= 1) return null;
  if (!tree.profiles.some((p) => p.id === profileId)) return tree;
  return {
    ...tree,
    profiles: tree.profiles.filter((p) => p.id !== profileId),
    meta: { ...tree.meta, lastWriteAt: now },
  };
}

// Pick the next sortIndex above any existing profile so the new card lands
// at the end of the picker grid.
export function nextProfileSortIndex(tree: SyncedTree): number {
  if (tree.profiles.length === 0) return 0;
  return Math.max(...tree.profiles.map((p) => p.sortIndex)) + 1;
}
